import type { useAlert } from '@ads/components-react';
import type { QueryClient } from '@tanstack/react-query';
import type { MutableRefObject, SetStateAction } from 'react';
import { useCallback } from 'react';
import {
  getApiV1SsoimageIdQueryKey,
  type SSOImageRepositoryResponse,
  type usePutApiV1SsoimageId,
} from '@/shared/api/generated';
import { getApiV1SsoimageQueryKey } from '@/shared/api/generated/hooks/SSOImageController/useGetApiV1Ssoimage';
import type { ESSOCategory } from '@/shared/api/generated/types/ESSOCategory';
import type { UpdateSSOImageLangViewModel } from '@/shared/api/generated/types/UpdateSSOImageLangViewModel';
import type { SlideData, VariantKey } from '../store/atoms/slideAtom';
import type { LibrarySlide } from '../store/atoms/slideLibraryAtom';
import { REQUIRED_VARIANTS } from '../utils/isSlideComplete';

export interface UseSaveToLibraryParams {
  readonly slideId: number | undefined;
  readonly variantKey: VariantKey;
  readonly activeCategoryId: number | undefined;
  readonly currentSlide: SSOImageRepositoryResponse | undefined;
  readonly slideRef: MutableRefObject<SlideData>;
  readonly queryClient: QueryClient;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly setLibrarySlides: (update: SetStateAction<LibrarySlide[]>) => void;
  readonly updateSsoimage: ReturnType<typeof usePutApiV1SsoimageId>['mutate'];
  readonly alert: ReturnType<typeof useAlert>['alert'];
  readonly setIsDirty: (dirty: boolean) => void;
}

/**
 * Persiste metadados e conteúdo do slide ativo no backend via PUT, atualiza o
 * cache local da biblioteca e invalida as queries afetadas.
 *
 * @param params - Slide ativo, dados da API e dependências de mutação/cache
 * @returns `saveToLibrary` — dispara o save do slide ativo
 */
export function useSaveToLibrary({
  slideId,
  variantKey,
  activeCategoryId,
  currentSlide,
  slideRef,
  queryClient,
  saveCurrentSlide,
  setLibrarySlides,
  updateSsoimage,
  alert,
  setIsDirty,
}: UseSaveToLibraryParams) {
  return useCallback(() => {
    saveCurrentSlide(variantKey);

    // Pequeno timeout para garantir que setSlide (async via Jotai) já foi processado
    setTimeout(() => {
      const slide = slideRef.current;
      const now = new Date().toISOString();
      const numericCategoryId = Number(activeCategoryId ?? 0) as ESSOCategory;

      // Atualiza cache local com campos válidos
      if (slideId) {
        setLibrarySlides(prev =>
          prev.map(s => (s.id !== slideId ? s : { ...s, title: slide.name, dateEnd: now })),
        );
      }

      // Constrói array de langs para o PUT.
      // Cada item inclui o `id` do lang já persistido (vindo de currentSlide.ssoImageLangs),
      // correspondido por cultureInfo + posição: índice 0 = desktop (viewPort 1), índice 1 = mobile (viewPort 2).
      //
      // Regra de id:
      //   - Lang existente no backend (id > 0)  → envia { id, ... }  → backend faz UPDATE
      //   - Lang ainda não criada (sem id)       → omite o campo id   → backend faz INSERT
      //   - NUNCA enviar id = 0 (backend rejeita: "Idioma com Id 0 não encontrado")
      //
      // Prioridade de conteúdo por variant:
      //   1. Jotai (variant.canvas) — variante editada/carregada nesta sessão
      //   2. Cache TanStack Query (['json-data', urlStorage]) — variante previamente carregada mas não editada
      //
      // Se nenhuma fonte tem conteúdo para uma variante, ela é omitida.
      const langs: UpdateSSOImageLangViewModel[] = REQUIRED_VARIANTS.flatMap(
        ({ cultureInfo, viewPort, variantKey: vKey }) => {
          const filteredLangs =
            currentSlide?.ssoImageLangs?.filter(item => item.cultureInfo === cultureInfo) ?? [];
          const existingLang = filteredLangs[viewPort === 1 ? 0 : 1];
          // id só é incluído quando realmente existir no backend (> 0)
          const idField = existingLang?.id ? { id: existingLang.id } : {};

          // Prioridade 1: conteúdo em memória (Jotai) — variante editada nesta sessão
          const variant = slide.variants[vKey];

          if (variant?.canvas) {
            const canvasJson = {
              ...variant.canvas,
              backgroundColor: variant.backgroundColor,
              ...(variant.backgroundVideo ? { videoSrc: variant.backgroundVideo } : {}),
            };
            return [
              {
                ...idField,
                cultureInfo,
                file: new Blob([JSON.stringify(canvasJson)], { type: 'application/json' }),
                viewPort,
              } as UpdateSSOImageLangViewModel,
            ];
          }

          // Prioridade 2: cache TanStack Query — variante carregada da API mas não editada nesta sessão
          if (existingLang?.urlStorage) {
            const cached = queryClient.getQueryData<Record<string, unknown>>([
              'json-data',
              existingLang.urlStorage,
            ]);
            if (cached) {
              return [
                {
                  ...idField,
                  cultureInfo,
                  file: new Blob([JSON.stringify(cached)], { type: 'application/json' }),
                  viewPort,
                } as UpdateSSOImageLangViewModel,
              ];
            }
          }

          return [];
        },
      );

      // Persiste metadados e conteúdo do canvas no backend
      updateSsoimage(
        {
          id: Number(slide.id),
          data: {
            Title: slide.name,
            CategoryId: numericCategoryId,
            Langs: langs,
            ...(slide.displayPeriod?.startDate && {
              DateStart: new Date(slide.displayPeriod.startDate).toISOString(),
            }),
            ...(slide.displayPeriod?.endDate && {
              DateEnd: new Date(slide.displayPeriod.endDate).toISOString(),
            }),
            ...(slide.displayPeriod?.startDate && {
              DateStart: new Date(slide.displayPeriod.startDate).toISOString(),
            }),
            ...(slide.displayPeriod?.endDate && {
              DateEnd: new Date(slide.displayPeriod.endDate).toISOString(),
            }),
          },
        },
        {
          onSuccess: () => {
            setIsDirty(false);
            // Invalida o GET do slide para refletir as novas urlStorage geradas pelo backend
            if (slideId) {
              void queryClient.invalidateQueries({
                queryKey: getApiV1SsoimageIdQueryKey(Number(slideId)),
              });
            }
            // Invalida a listagem para refletir título e estado atualizados na biblioteca
            void queryClient.invalidateQueries({ queryKey: getApiV1SsoimageQueryKey() });
            // Invalida todos os caches de JSON externo (canvas das variantes)
            void queryClient.invalidateQueries({ queryKey: ['json-data'] });
            alert({ alertType: 'information', title: 'Slide salvo com sucesso' });
          },
          onError: () => {
            alert({ alertType: 'danger', title: 'Erro ao salvar o slide' });
          },
        },
      );
    }, 100);
  }, [
    saveCurrentSlide,
    variantKey,
    slideId,
    activeCategoryId,
    setLibrarySlides,
    updateSsoimage,
    queryClient,
    alert,
    currentSlide?.ssoImageLangs?.filter,
    slideRef,
    setIsDirty,
  ]);
}
